/**
 * Converting between the two shapes a job description comes in.
 *
 * Lives apart from RichTextEditor because the public embed board needs to
 * render a description without dragging CKEditor into a page that candidates
 * load on someone else's website.
 *
 * Anything stored since the rich-text editor landed is already HTML, sanitised
 * server-side on save. Anything older is markdown-ish plain text.
 */

/** Markdown-ish plain text to HTML. HTML input passes through untouched. */
export function plainTextToHtml(text) {
  if (!text) return '';
  if (/<(p|h[2-4]|ul|ol|li|br|strong|em|blockquote)\b/i.test(text)) return text;

  const esc = (s) => s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (s) => esc(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+?)\*/g, '$1<em>$2</em>');

  const out = [];
  let list = null;                     // 'ul' | 'ol' | null
  const closeList = () => { if (list) { out.push(`</${list}>`); list = null; } };

  for (const raw of String(text).split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) { closeList(); continue; }

    if (/^---+$/.test(line)) { closeList(); out.push('<hr>'); continue; }

    const heading = line.match(/^(#{2,4})\s+(.*)$/);
    if (heading) {
      closeList();
      const level = Math.min(4, heading[1].length);
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`);
      continue;
    }

    const bullet = line.match(/^[•*-]\s+(.*)$/);
    if (bullet) {
      if (list !== 'ul') { closeList(); out.push('<ul>'); list = 'ul'; }
      out.push(`<li>${inline(bullet[1])}</li>`);
      continue;
    }

    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    if (numbered) {
      if (list !== 'ol') { closeList(); out.push('<ol>'); list = 'ol'; }
      out.push(`<li>${inline(numbered[1])}</li>`);
      continue;
    }

    closeList();
    out.push(`<p>${inline(line)}</p>`);
  }
  closeList();
  return out.join('');
}

/** Strip tags, for counting characters or showing a short preview. */
export function htmlToPlainText(html) {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|h[2-4]|li|blockquote)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .trim();
}
